/**
 * @format
 */
// Migrated from AngularJS: moduleAttainmentApplication.service.js
import { Injectable } from '@angular/core';
import { ModulePlanState, PlanValidationResult, PlanValidationTs, ValidatablePlan } from 'common-typescript';
import {
    Module,
    ModuleAttainmentApplication,
    PlanValidationState,
    StudentWorkflow,
    StudyModule,
    StudyRight,
} from 'common-typescript/types';
import { forkJoin, Observable, of, OperatorFunction, throwError } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { AuthService } from 'sis-common';
import { AppErrorHandler, STUDENT_APPLICATION_STATE, StudentApplicationEntityService, WorkflowEntityService } from 'sis-components';

@Injectable({
    providedIn: 'root',
})
export class ModuleAttainmentApplicationService {
    constructor(
        private authService: AuthService,
        private studentApplicationEntityService: StudentApplicationEntityService,
        private workflowEntityService: WorkflowEntityService,
        private appErrorHandler: AppErrorHandler,
    ) {}

    /**
     * Ensures moduleAttainmentApplications exist for the module and all child modules.
     * If a module already has an application in REQUESTED or IN_HANDLING state, the existing one is returned.
     * An application is sent for any module not yet attained and without an existing application.
     * @returns Observable of the created or existing application for the given module
     */
    sendApplication(
        module: Module,
        validatablePlan: ValidatablePlan,
        studyRight: StudyRight,
        planValidationResult: PlanValidationResult,
        sentAsDPChildApplication: boolean,
        errorHandler: OperatorFunction<StudentWorkflow, StudentWorkflow> = this.appErrorHandler.defaultErrorHandler(),
    ): Observable<ModuleAttainmentApplication | StudentWorkflow | undefined> {
        if (!sentAsDPChildApplication) {
            if (isLikeGroupingModule(module)) {
                return throwError(() => `Module ${module.id} is a grouping or non graded module.`);
            }
            if (!this.isPlanStateValidForApplicationSending(module, validatablePlan, planValidationResult, sentAsDPChildApplication)) {
                return throwError(() => `Module ${module.id} is not in correct plan state.`);
            }
        }

        return this.studentApplicationEntityService
            .getApplicationsForStudentByTypes(this.authService.personId(), ['MODULE_ATTAINMENT_APPLICATION'])
            .pipe(
                take(1),
                switchMap(applications => {
                    const existingApplications = (applications as ModuleAttainmentApplication[]).filter(
                        app => app.state === STUDENT_APPLICATION_STATE.REQUESTED || app.state === STUDENT_APPLICATION_STATE.IN_HANDLING,
                    );
                    return this.recursiveApplicationSender(
                        module,
                        validatablePlan,
                        studyRight,
                        existingApplications,
                        planValidationResult,
                        sentAsDPChildApplication,
                        errorHandler,
                    );
                }),
            );
    }

    planStateIsValidForSendingApplication(planState: ModulePlanState | null, sentAsDPChildApplication: boolean): boolean {
        return sentAsDPChildApplication ? isModulePlannedForAttainment(planState) : isEveryPartOfModuleAttained(planState);
    }

    /**
     * Searches for module attainment applications by module id for the current user.
     */
    searchExistingApplicationsForModule(module: Module): Observable<ModuleAttainmentApplication[]> {
        return this.studentApplicationEntityService
            .getApplicationsForStudentByTypes(this.authService.personId(), ['MODULE_ATTAINMENT_APPLICATION'])
            .pipe(
                take(1),
                map(applications =>
                    (applications as ModuleAttainmentApplication[]).filter(
                        (app: ModuleAttainmentApplication) => app.moduleId === module.id,
                    ),
                ),
            );
    }

    createModuleOrDegreeProgrammeAttainmentWorkflow(
        workflowApplication: Partial<ModuleAttainmentApplication>,
        errorHandler: OperatorFunction<StudentWorkflow, StudentWorkflow> = this.appErrorHandler.defaultErrorHandler(),
    ): Observable<StudentWorkflow> {
        return this.workflowEntityService.createAsStudent(workflowApplication).pipe(take(1), errorHandler);
    }

    private isPlanStateValidForApplicationSending(
        module: Module,
        validatablePlan: ValidatablePlan,
        planValidationResult: PlanValidationResult,
        sentAsDPChildApplication: boolean,
    ): boolean {
        const planState = PlanValidationTs.getModulePlanState(module, validatablePlan, planValidationResult);
        return this.planStateIsValidForSendingApplication(planState, sentAsDPChildApplication);
    }

    private recursiveApplicationSender(
        module: Module,
        validatablePlan: ValidatablePlan,
        studyRight: StudyRight,
        existingApplications: ModuleAttainmentApplication[],
        planValidationResult: PlanValidationResult,
        sentAsDPChildApplication: boolean,
        errorHandler: OperatorFunction<StudentWorkflow, StudentWorkflow>,
    ): Observable<ModuleAttainmentApplication | StudentWorkflow | undefined> {
        const existingApplication = existingApplications.find(app => app.moduleId === module.id);
        if (existingApplication) {
            return of(existingApplication);
        }

        const childModules: Module[] = validatablePlan.getSelectedModulesUnderModule(module);
        const qualifyingChildren = childModules.filter(
            child =>
                isLikeGroupingModule(child) ||
                this.isPlanStateValidForApplicationSending(child, validatablePlan, planValidationResult, sentAsDPChildApplication),
        );
        const children$ =
            qualifyingChildren.length > 0
                ? forkJoin(
                      qualifyingChildren.map(child =>
                          this.recursiveApplicationSender(
                              child,
                              validatablePlan,
                              studyRight,
                              existingApplications,
                              planValidationResult,
                              sentAsDPChildApplication,
                              errorHandler,
                          ),
                      ),
                  )
                : of([]);

        return children$.pipe(
            switchMap((): Observable<ModuleAttainmentApplication | StudentWorkflow | undefined> => {
                if (
                    isLikeGroupingModule(module) ||
                    !this.isPlanStateValidForApplicationSending(module, validatablePlan, planValidationResult, sentAsDPChildApplication)
                ) {
                    return of(undefined);
                }
                return this.createModuleOrDegreeProgrammeAttainmentWorkflow(
                    createWorkflowApplication(module, validatablePlan, studyRight),
                    errorHandler,
                );
            }),
        );
    }
}

function isLikeGroupingModule(module: Module): boolean {
    return module.type === 'GroupingModule' || (module.type === 'StudyModule' && (module as StudyModule).graded === false);
}

function createWorkflowApplication(
    module: Module,
    validatablePlan: ValidatablePlan,
    studyRight: StudyRight,
): Partial<ModuleAttainmentApplication> {
    const { plan } = validatablePlan;
    return {
        moduleId: module.id,
        planId: plan.id,
        studyRightId: studyRight.id,
        state: 'REQUESTED',
        type: 'ModuleAttainmentApplication',
        planContent: {
            courseUnitSelections: structuredClone(plan.courseUnitSelections),
            moduleSelections: structuredClone(plan.moduleSelections),
            customCourseUnitAttainmentSelections: structuredClone(plan.customCourseUnitAttainmentSelections),
            customModuleAttainmentSelections: structuredClone(plan.customModuleAttainmentSelections),
            customStudyDrafts: structuredClone(plan.customStudyDrafts),
        },
    };
}

/**
 * Whether the module is planned or partly attained, so that it can be applied for with its degree programme. A module content approval,
 * if the module has one, must be requested or accepted: one in handling doesn't do.
 */
export function isModulePlannedForAttainment(planState: ModulePlanState | null): boolean {
    switch (planState?.contentApproval) {
        case 'REQUESTED':
        case 'ACCEPTED':
        case 'CONDITIONAL':
            return true;
        case 'NONE':
            return [PlanValidationState.PLANNED, PlanValidationState.PARTS_ATTAINED, PlanValidationState.IMPLICIT].includes(
                planState.validationState,
            );
        default:
            return false;
    }
}

/** Whether every part of the module is attained, so that it can be applied for on its own. A module content approval must be accepted. */
export function isEveryPartOfModuleAttained(planState: ModulePlanState | null): boolean {
    return (
        planState?.validationState === PlanValidationState.PARTS_ATTAINED &&
        ['NONE', 'ACCEPTED', 'CONDITIONAL'].includes(planState.contentApproval)
    );
}
